import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

// PDF Styles for Purchase Invoice
const pdfStyles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: "#0891b2",
    paddingBottom: 20,
  },
  companyName: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#0891b2",
  },
  companyDetails: {
    fontSize: 10,
    color: "#666",
    marginTop: 5,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0891b2",
    textAlign: "right",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    textAlign: "right",
    marginTop: 5,
  },
  infoSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    backgroundColor: "#f8f9fa",
    padding: 5,
    borderRadius: 5,
  },
  infoBlock: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 10,
    color: "#666",
    marginBottom: 3,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
  },
  supplierSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#0891b2",
    color: "white",
    padding: 10,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    padding: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9f9f9",
  },
  col1: { width: "35%", paddingHorizontal: 5 },
  col2: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col3: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col4: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  col5: { width: "10%", textAlign: "center", paddingHorizontal: 5 },
  col6: { width: "15%", textAlign: "right", paddingHorizontal: 5 },
  totals: {
    marginTop: 20,
    alignItems: "flex-end",
  },
  totalRow: {
    flexDirection: "row",
    padding: 5,
    width: "50%",
  },
  totalLabel: {
    flex: 1,
    textAlign: "right",
    marginRight: 10,
    fontSize: 11,
  },
  totalValue: {
    width: 120,
    textAlign: "right",
    fontSize: 11,
  },
  notes: {
    marginTop: 30,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  notesTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#0891b2",
  },
  notesText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: "#666",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    color: "#666",
    fontSize: 9,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    paddingTop: 10,
  },
  compliance: {
    fontSize: 8,
    color: "#0891b2",
    marginTop: 5,
  },
});

// PDF Document Component for Purchase Invoice
const PurchaseInvoicePDFDocument = ({ invoiceData }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: invoiceData.currency || "AED",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
            <Text style={pdfStyles.companyName}>
              Diamond Quality Constructions
            </Text>
            <Text style={pdfStyles.companyDetails}>Sharjah, UAE</Text>
            <Text style={pdfStyles.companyDetails}>TRN: 104836477000001</Text>
          </View>
          <View>
            <Text style={pdfStyles.invoiceTitle}>PURCHASE INVOICE</Text>
            <Text style={pdfStyles.subtitle}>UAE Federal Tax Authority</Text>
          </View>
        </View>

        {/* PO Info */}
        <View style={pdfStyles.infoSection}>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Number</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.poNumber}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Invoice Date</Text>
            <Text style={pdfStyles.infoValue}>
              {formatDate(invoiceData.poDate)}
            </Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Payment Status</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.status}</Text>
          </View>
          <View style={pdfStyles.infoBlock}>
            <Text style={pdfStyles.infoLabel}>Mode of Payment</Text>
            <Text style={pdfStyles.infoValue}>{invoiceData.mode || "-"}</Text>
          </View>
        </View>

        {/* Supplier Details */}
        <View style={pdfStyles.supplierSection}>
          <Text style={pdfStyles.infoLabel}>Supplier:</Text>
          <Text
            style={[pdfStyles.infoValue, { marginTop: 5, marginBottom: 3 }]}
          >
            {invoiceData.supplierName}
          </Text>
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.supplierAddress}
          </Text>
          {invoiceData.supplierTrn && (
            <Text style={pdfStyles.companyDetails}>
              TRN: {invoiceData.supplierTrn}
            </Text>
          )}
          <Text style={pdfStyles.companyDetails}>
            {invoiceData.supplierCity}, {invoiceData.supplierCountry}
          </Text>
          {invoiceData.supplierPhone && (
            <Text style={pdfStyles.companyDetails}>
              Phone: {invoiceData.supplierPhone}
            </Text>
          )}
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={pdfStyles.col1}>Description</Text>
            <Text style={pdfStyles.col2}>Qty</Text>
            <Text style={pdfStyles.col3}>Unit Price</Text>
            <Text style={pdfStyles.col4}>Discount</Text>
            <Text style={pdfStyles.col5}>VAT</Text>
            <Text style={pdfStyles.col6}>Total</Text>
          </View>

          {invoiceData.items.map((item, index) => {
            const subtotal = item.quantity * item.unit_price;
            const discountAmount = item.discount_amount || 0;
            const afterDiscount = subtotal - discountAmount;
            const vatAmount = afterDiscount * (item.vat_percent / 100);
            const total = afterDiscount + vatAmount;

            return (
              <View
                key={index}
                style={[
                  pdfStyles.tableRow,
                  index % 2 === 1 ? pdfStyles.tableRowAlt : {},
                ]}
              >
                <Text style={pdfStyles.col1}>{item.description}</Text>
                <Text style={pdfStyles.col2}>
                  {item.quantity} {item.unit}
                </Text>
                <Text style={pdfStyles.col3}>
                  {formatCurrency(item.unit_price)}
                </Text>
                <Text style={pdfStyles.col4}>
                  {formatCurrency(discountAmount)}
                </Text>
                <Text style={pdfStyles.col5}>{item.vat_percent}%</Text>
                <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={pdfStyles.totals}>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Subtotal:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals?.subtotal || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Total Discount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals?.totalDiscount || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>Taxable Amount:</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals?.taxableAmount || 0)}
            </Text>
          </View>
          <View style={pdfStyles.totalRow}>
            <Text style={pdfStyles.totalLabel}>VAT (5%):</Text>
            <Text style={pdfStyles.totalValue}>
              {formatCurrency(invoiceData.totals?.vatTotal || 0)}
            </Text>
          </View>
          <View
            style={[
              pdfStyles.totalRow,
              {
                marginTop: 10,
                borderTopWidth: 2,
                borderTopColor: "#0891b2",
                paddingTop: 10,
              },
            ]}
          >
            <Text
              style={[
                pdfStyles.totalLabel,
                { fontWeight: "bold", fontSize: 12 },
              ]}
            >
              TOTAL:
            </Text>
            <Text
              style={[
                pdfStyles.totalValue,
                { fontWeight: "bold", fontSize: 12, color: "#0891b2" },
              ]}
            >
              {formatCurrency(invoiceData.totals?.totalDue || 0)}
            </Text>
          </View>
        </View>

        {/* Notes */}
        {invoiceData.notes && (
          <View style={pdfStyles.notes}>
            <Text style={pdfStyles.notesTitle}>Notes / Terms:</Text>
            <Text style={pdfStyles.notesText}>{invoiceData.notes}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={pdfStyles.footer}>
          <Text>
            This is a computer generated purchase invoice. No signature
            required.
          </Text>
          <Text style={pdfStyles.compliance}>
            🇦🇪 UAE VAT Compliant | Federal Tax Authority Approved | TRN:
            104836477000001
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const EditablePurchaseVATInvoice = ({ initialData, onSubmit, companyInfo }) => {
  const [formData, setFormData] = useState(
    initialData || {
      poNumber: "PO-001",
      poDate: "",
      status: "Unpaid",
      mode: null,
      supplierId: null,
      customer_id: "",
      customerName: "",
      currency: "AED",

      // Supplier Information
      supplierName: "",
      supplierTrn: "",
      supplierPhone: "",
      supplierAddress: "",
      supplierCity: "",
      supplierCountry: "",

      // Notes
      notes: "",
    },
  );

  const [company] = useState(
    companyInfo || {
      name: "Diamond Quality Constructions",
      trn: "104836477000001",
      address: "Sharjah, UAE",
    },
  );

  const [items, setItems] = useState(initialData?.items || []);
  const [errors, setErrors] = useState({});
  const [pdfData, setPdfData] = useState(null);
  const [shouldDownload, setShouldDownload] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);
  const [itemOptions, setItemOptions] = useState([]);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;

  // Debug: Log the structure of passed data
  useEffect(() => {
    console.log("📊 EditablePurchaseVATInvoice - Data Structure Received:");
    console.log("===================================================");
    console.log("📋 initialData (Full Object):", initialData);
    console.log("---");
    console.log(
      "📋 initialData Keys:",
      initialData ? Object.keys(initialData) : "No initialData",
    );
    console.log("---");
    console.log("Data Breakdown:");
    if (initialData) {
      console.log("  - ID:", initialData.id);
      console.log("  - PO Number:", initialData.poNumber);
      console.log("  - PO Date:", initialData.poDate);
      console.log("  - Status:", initialData.status);
      console.log("  - Mode:", initialData.mode);
      console.log("  - Supplier ID:", initialData.supplierId);
      console.log("  - Customer ID:", initialData.customer_id);
      console.log("  - Customer Name:", initialData.customerName);
      console.log("  - Supplier Name:", initialData.supplierName);
      console.log("  - Supplier TRN:", initialData.supplierTrn);
      console.log("  - Supplier Phone:", initialData.supplierPhone);
      console.log("  - Supplier Address:", initialData.supplierAddress);
      console.log("  - Supplier City:", initialData.supplierCity);
      console.log("  - Supplier Country:", initialData.supplierCountry);
      console.log("  - Notes:", initialData.notes);
      console.log("  - Currency:", initialData.currency);
      console.log("  - Items Count:", initialData.items?.length || 0);
      if (initialData.items && initialData.items.length > 0) {
        console.log("  - First Item:", initialData.items[0]);
      }
    }
    console.log("---");
    console.log("📋 companyInfo:", companyInfo);
    console.log("---");
    console.log("📋 onSubmit type:", typeof onSubmit);
    console.log("===================================================");
  }, [initialData, companyInfo, onSubmit]);

  // UAE Emirates list
  const emirates = [
    "Abu Dhabi",
    "Dubai",
    "Sharjah",
    "Ajman",
    "Umm Al Quwain",
    "Ras Al Khaimah",
    "Fujairah",
  ];

  // Handle file selection
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert("File size must be less than 5MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  // Clear file selection
  const clearFileSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Calculate line item total
  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discountAmount = item.discount_amount;
    const afterDiscount = subtotal - discountAmount;
    const vatAmount = afterDiscount * (item.vat_percent / 100);
    return afterDiscount + vatAmount;
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxableAmount = 0;
    let vatTotal = 0;

    items.forEach((item) => {
      const itemSubtotal = item.quantity * item.unit_price;
      const itemDiscount = item.discount_amount;
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemVat = itemAfterDiscount * (item.vat_percent / 100);

      subtotal += itemSubtotal;
      totalDiscount += itemDiscount;
      taxableAmount += itemAfterDiscount;
      vatTotal += itemVat;
    });

    const totalDue = taxableAmount + vatTotal;

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      vatTotal,
      totalDue,
    };
  };

  const { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue } =
    calculateTotals();

  // Add new item row
  const addItem = () => {
    setItems([
      ...items,
      {
        description: "",
        quantity: 1,
        unit: "Unit",
        unit_price: 0,
        discount_amount: 0,
        vat_percent: 5,
      },
    ]);
  };

  // Remove item row
  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    }
  };

  // Update item field
  const updateItem = (index, field, value) => {
    const newItems = [...items];

    if (
      ["quantity", "unit_price", "discount_amount", "vat_percent"].includes(
        field,
      )
    ) {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    setItems(newItems);
  };

  // Update form field
  const updateField = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "/");
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.supplierName)
      newErrors.supplierName = "Supplier name is required";
    if (!formData.poDate) newErrors.poDate = "PO date is required";

    items.forEach((item, index) => {
      if (!item.description) {
        newErrors[`item_${index}_description`] = "Description required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = "Quantity must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit - show modal first
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowSaveModal(true);
    }
  };

  // Handle actual invoice update
  const saveInvoice = async (downloadPDF = false) => {
    setShowSaveModal(false);
    setIsSubmitting(true);

    try {
      // Prepare API payload for update
      let updatePayload = {
        supplier_id: formData.supplierId,
        customer_id: formData.customer_id,
        po_date: formData.poDate,
        status: formData.status,
        mode: formData.status === "Unpaid" ? "" : formData.mode || "",
        notes: formData.notes,
        items: items.map((item) => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0,
          discount_amount: parseFloat(item.discount_amount) || 0,
          vat_percent: parseFloat(item.vat_percent) || 0,
        })),
      };

      // If file is selected, use FormData instead
      if (selectedFile) {
        console.log("📸 Creating FormData with file:", selectedFile.name);

        const formDataToSend = new FormData();

        // Laravel method override for PUT with FormData
        formDataToSend.append("_method", "PUT");

        formDataToSend.append("supplier_id", updatePayload.supplier_id);
        formDataToSend.append("customer_id", updatePayload.customer_id);
        formDataToSend.append("po_date", updatePayload.po_date);
        formDataToSend.append("status", updatePayload.status);
        formDataToSend.append("mode", updatePayload.mode);
        formDataToSend.append("notes", updatePayload.notes);

        // CRITICAL: Append image and verify
        formDataToSend.append("image", selectedFile);
        console.log("📸 After appending image:");
        console.log(
          '  - formDataToSend.has("image"):',
          formDataToSend.has("image"),
        );
        console.log(
          '  - formDataToSend.get("image"):',
          formDataToSend.get("image"),
        );
        console.log("  - Selected file object:", selectedFile);

        updatePayload.items.forEach((item, index) => {
          formDataToSend.append(
            `items[${index}][description]`,
            item.description,
          );
          formDataToSend.append(`items[${index}][quantity]`, item.quantity);
          formDataToSend.append(`items[${index}][unit]`, item.unit);
          formDataToSend.append(`items[${index}][unit_price]`, item.unit_price);
          formDataToSend.append(
            `items[${index}][discount_amount]`,
            item.discount_amount,
          );
          formDataToSend.append(
            `items[${index}][vat_percent]`,
            item.vat_percent,
          );
        });

        console.log("📸 FormData content:");
        for (let [key, value] of formDataToSend.entries()) {
          console.log(
            `  - ${key}:`,
            value instanceof File ? `[File: ${value.name}]` : value,
          );
        }

        updatePayload = formDataToSend;
      } else {
        console.log("❌ NO FILE SELECTED");
      }

      console.log(
        "📸 Final payload type:",
        updatePayload instanceof FormData ? "FormData" : "JSON",
      );

      const method = selectedFile ? "post" : "put";
      const response = await axios[method](
        `${apiUrl}/api/pos/${formData.id}`,
        updatePayload,
      );
      console.log("✅ Update Success:", response.data);

      if (response.data?.data?.image) {
        console.log("✅ Image path returned:", response.data.data.image);
        clearFileSelection();
      }

      // Prepare display data for PDF
      const invoiceData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
        formattedDates: {
          poDate: formatDateForDisplay(formData.poDate),
        },
      };

      // Prepare PDF data
      const pdfInvoiceData = {
        ...formData,
        items: items,
        company: company,
        totals: { subtotal, totalDiscount, taxableAmount, vatTotal, totalDue },
      };

      // Set PDF data and trigger download only if user selected the option
      if (downloadPDF) {
        setPdfData(pdfInvoiceData);
        setShouldDownload(true);
      }

      // Call onSubmit callback if provided
      if (onSubmit && onSubmit !== (() => {})) {
        onSubmit(invoiceData);
      } else {
        // Default behavior: show success message
        if (downloadPDF) {
          alert(
            "Purchase Invoice updated successfully! PDF will be downloaded automatically.",
          );
        } else {
          setSuccessMessage(formData.poNumber);
        }
      }
    } catch (error) {
      console.error("Error updating purchase invoice:", error);
      alert(
        error.response?.data?.message ||
          "Error updating purchase invoice. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effect to fetch customer information when customer_id is available
  useEffect(() => {
    const fetchCustomerInfo = async () => {
      if (formData.customer_id && !formData.customerName) {
        try {
          console.log(
            "📍 Fetching customer info for ID:",
            formData.customer_id,
          );
          const response = await axios.get(
            `${apiUrl}/api/customers/${formData.customer_id}`,
          );
          console.log("📍 Customer response:", response.data);

          const customer = response.data.data || response.data;
          console.log("📍 Extracted customer:", customer);

          if (customer && customer.name) {
            setFormData((prev) => ({
              ...prev,
              customerName: customer.name || prev.customerName,
            }));
            console.log("✅ Customer name updated:", customer.name);
          }
        } catch (err) {
          console.warn("⚠️ Could not fetch customer info:", err.message);
          // Don't show error - just keep the customer_id visible
        }
      }
    };

    fetchCustomerInfo();
  }, [formData.customer_id, apiUrl]);

  // Effect to handle PDF download
  useEffect(() => {
    if (shouldDownload && pdfData) {
      const generateAndDownloadPDF = async () => {
        try {
          const blob = await pdf(
            <PurchaseInvoicePDFDocument invoiceData={pdfData} />,
          ).toBlob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const fileName = `PurchaseInvoice-${pdfData.poNumber || "Updated"}.pdf`;
          link.download = fileName;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          // Reset the trigger
          setShouldDownload(false);
          setPdfData(null);
        } catch (error) {
          console.error("Error generating PDF:", error);
          alert("Error generating PDF. Please try again.");
          setShouldDownload(false);
          setPdfData(null);
        }
      };

      generateAndDownloadPDF();
    }
  }, [shouldDownload, pdfData]);

  // Fetch items for autocomplete
  useEffect(() => {
    const fetchItems = async () => {
      if (!apiUrl) {
        console.warn("API URL not configured, skipping items fetch");
        setItemOptions([]);
        return;
      }

      try {
        const response = await axios.get(`${apiUrl}/api/getpoitems`);
        console.log("Items API Response:", response.data);

        // Handle both response formats
        let itemsArray = [];
        if (response.data && Array.isArray(response.data)) {
          itemsArray = response.data;
        } else if (
          response.data &&
          response.data.data &&
          Array.isArray(response.data.data)
        ) {
          itemsArray = response.data.data;
        } else {
          itemsArray = [];
        }

        setItemOptions(itemsArray);
        console.log("Loaded item suggestions:", itemsArray.length);
      } catch (error) {
        console.error("Error fetching items:", error);
        setItemOptions([]);
      }
    };

    fetchItems();
  }, [apiUrl]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Main Form Card */}
        <div className="bg-base-100 rounded-b-2xl shadow-2xl p-8 mb-8 border-t-4 border-primary">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Purchase Order Details Section */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-primary rounded-full"></span>
                Purchase Order Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-primary">📄</span> PO NO.
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 focus:border-primary"
                    value={formData.poNumber}
                    onChange={(e) => updateField("poNumber", e.target.value)}
                    placeholder="PO-00001"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-secondary">📅</span> PO DATE
                    </span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered border-2 focus:border-secondary ${
                      errors.poDate ? "input-error" : ""
                    }`}
                    value={formData.poDate}
                    onChange={(e) => updateField("poDate", e.target.value)}
                  />
                  {errors.poDate && (
                    <span className="text-error text-sm mt-1">
                      {errors.poDate}
                    </span>
                  )}
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-info">💰</span> PAYMENT STATUS
                    </span>
                  </label>
                  <select
                    className="select select-bordered border-2 focus:border-info"
                    value={formData.status}
                    onChange={(e) => updateField("status", e.target.value)}
                  >
                    <option value="Paid">Paid</option>
                    <option value="Unpaid">Unpaid</option>
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold flex items-center gap-1">
                      <span className="text-accent">🏗️</span> SITE NAME
                      (CUSTOMER)
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input input-bordered border-2 border-accent focus:border-accent bg-amber-50 cursor-not-allowed flex-1 font-semibold text-lg"
                      value={formData.customerName || "Not Assigned"}
                      readOnly
                      placeholder="Site/customer name"
                      title="Customer name from the system"
                    />
                    <input
                      type="text"
                      className="input input-bordered border-2 border-accent focus:border-accent bg-amber-50 cursor-not-allowed w-20 font-semibold text-center"
                      value={formData.customer_id || "-"}
                      readOnly
                      placeholder="ID"
                      title={`Customer ID: ${formData.customer_id}`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    📌 Site name and ID cannot be changed
                  </p>
                </div>

                <div className="form-control hidden">
                  <label className="label">
                    <span className="label-text font-semibold">
                      MODE OF PAYMENT{" "}
                      {formData.status === "Paid" && (
                        <span className="text-error">*</span>
                      )}
                    </span>
                  </label>
                  <select
                    className={`select select-bordered border-2 ${
                      formData.status === "Paid"
                        ? `focus:border-info ${errors.mode ? "select-error" : ""}`
                        : "opacity-50 cursor-not-allowed bg-base-200"
                    } ${errors.mode ? "select-error" : ""}`}
                    value={formData.mode || ""}
                    onChange={(e) =>
                      updateField("mode", e.target.value || null)
                    }
                    disabled={formData.status !== "Paid"}
                  >
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                  {errors.mode && (
                    <span className="text-error text-sm mt-1">
                      {errors.mode}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Supplier Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-secondary flex items-center gap-2">
                <span className="w-1 h-6 bg-secondary rounded-full"></span>
                Supplier Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      SUPPLIER NAME *
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered border-2 bg-base-200 cursor-not-allowed ${
                      errors.supplierName ? "input-error" : ""
                    }`}
                    value={formData.supplierName}
                    readOnly
                    placeholder="Supplier name"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cannot be changed
                  </p>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      SUPPLIER TRN
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.supplierTrn}
                    readOnly
                    placeholder="123456789012345"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      PHONE NUMBER
                    </span>
                  </label>
                  <input
                    type="tel"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.supplierPhone}
                    readOnly
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>

                <div className="form-control md:col-span-2">
                  <label className="label">
                    <span className="label-text font-semibold">ADDRESS</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.supplierAddress}
                    readOnly
                    placeholder="Street address"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">CITY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.supplierCity}
                    readOnly
                    placeholder="City"
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">COUNTRY</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered border-2 bg-base-200 cursor-not-allowed"
                    value={formData.supplierCountry}
                    readOnly
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="bg-gradient-to-r from-accent/10 to-primary/10 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-accent flex items-center gap-2">
                  <span className="w-1 h-6 bg-accent rounded-full"></span>
                  Line Items
                </h3>
                <button
                  type="button"
                  onClick={addItem}
                  className="btn btn-sm btn-outline btn-primary"
                >
                  ➕ Add Item
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="table table-compact w-full">
                  <thead>
                    <tr className="bg-base-200">
                      <th className="w-1/4">Description</th>
                      <th className="w-16">Qty</th>
                      <th className="w-20">Unit</th>
                      <th className="w-20">Unit Price</th>
                      <th className="w-20">Discount</th>
                      <th className="w-16">VAT %</th>
                      <th className="w-20">Total</th>
                      <th className="w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="hover:bg-base-200">
                        <td>
                          <div className="relative">
                            <input
                              list={`description-suggestions-${index}`}
                              className={`input input-sm input-bordered w-full ${
                                errors[`item_${index}_description`]
                                  ? "input-error"
                                  : ""
                              }`}
                              value={item.description}
                              onChange={(e) =>
                                updateItem(index, "description", e.target.value)
                              }
                              placeholder="Description"
                            />
                            <datalist id={`description-suggestions-${index}`}>
                              {itemOptions.map((itemOption, i) => (
                                <option key={i} value={itemOption} />
                              ))}
                            </datalist>
                            {errors[`item_${index}_description`] && (
                              <div className="text-error text-xs mt-1">
                                {errors[`item_${index}_description`]}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            className={`input input-sm input-bordered w-full ${
                              errors[`item_${index}_quantity`]
                                ? "input-error"
                                : ""
                            }`}
                            value={item.quantity}
                            onChange={(e) =>
                              updateItem(index, "quantity", e.target.value)
                            }
                            min="1"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="input input-sm input-bordered w-full"
                            value={item.unit}
                            onChange={(e) =>
                              updateItem(index, "unit", e.target.value)
                            }
                            placeholder="Unit"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="input input-sm input-bordered w-full"
                            value={item.unit_price}
                            onChange={(e) =>
                              updateItem(index, "unit_price", e.target.value)
                            }
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="input input-sm input-bordered w-full"
                            value={item.discount_amount}
                            onChange={(e) =>
                              updateItem(
                                index,
                                "discount_amount",
                                e.target.value,
                              )
                            }
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="input input-sm input-bordered w-full"
                            value={item.vat_percent}
                            onChange={(e) =>
                              updateItem(index, "vat_percent", e.target.value)
                            }
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="font-bold text-primary">
                          {new Intl.NumberFormat("en-AE", {
                            style: "currency",
                            currency: "AED",
                          }).format(calculateItemTotal(item))}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-xs btn-error btn-outline"
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="bg-base-200 p-6 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Invoice Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Subtotal</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {new Intl.NumberFormat("en-AE", {
                      style: "currency",
                      currency: "AED",
                    }).format(subtotal)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total Discount</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {new Intl.NumberFormat("en-AE", {
                      style: "currency",
                      currency: "AED",
                    }).format(totalDiscount)}
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-sm text-gray-600">VAT (5%)</p>
                  <p className="text-2xl font-bold text-warning">
                    {new Intl.NumberFormat("en-AE", {
                      style: "currency",
                      currency: "AED",
                    }).format(vatTotal)}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-primary to-secondary p-4 rounded-lg text-white">
                  <p className="text-sm opacity-90">Total Due</p>
                  <p className="text-2xl font-bold">
                    {new Intl.NumberFormat("en-AE", {
                      style: "currency",
                      currency: "AED",
                    }).format(totalDue)}
                  </p>
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">NOTES / TERMS</span>
              </label>
              <textarea
                className="textarea textarea-bordered focus:textarea-primary"
                value={formData.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Add any special notes or terms..."
                rows="4"
              ></textarea>
            </div>

            {/* File Upload Section */}
            <div className="bg-gradient-to-br from-purple/10 to-purple/5 p-6 rounded-xl border-2 border-dashed border-purple/30">
              <h3 className="text-lg font-bold text-purple mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-purple rounded-full"></span>
                📎 Attachment (Optional)
              </h3>

              <div className="space-y-4">
                {/* File Input */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-semibold">
                      Select File
                    </span>
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="file-input file-input-bordered file-input-sm w-full"
                    onChange={handleFileUpload}
                    accept="*"
                  />
                  <label className="label">
                    <span className="label-text-alt text-xs">
                      Max file size: 5MB
                    </span>
                  </label>
                </div>

                {/* Selected File Display */}
                {selectedFile && (
                  <div className="bg-success/10 border-l-4 border-success p-3 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">✅</span>
                      <div>
                        <p className="font-semibold text-sm text-success">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-error"
                      onClick={clearFileSelection}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-between items-center gap-4">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "✓ Update Purchase Invoice"}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md p-6">
              <h2 className="text-xl font-bold mb-4">Save Changes?</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to update this purchase invoice?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={() => saveInvoice(false)}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => saveInvoice(true)}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save & Download PDF"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditablePurchaseVATInvoice;